/**
 * MONSTER TOWN — STATIC MESH MERGING. OWNED BY THE WORLD CREW (src/world/**).
 *
 * WHY THIS EXISTS: the town is the single biggest pile of geometry in the game
 * and it never animates. A cosy house is ~40 little boxes; four buildings, a
 * ring road, forty trees and a pond would be ~600 draw calls, which an older
 * iPad will not forgive.
 *
 * `mergeStatic()` takes a group built with the normal `src/render/kit.ts`
 * vocabulary and collapses it into ONE mesh per (material, shadow-flag) pair.
 * A whole house becomes 4-6 draw calls; a grove of twelve trees becomes 3.
 *
 * DELIBERATELY PER-CLUSTER, NOT ONE GIANT MESH: three.js frustum-culls per
 * object, so merging by cluster keeps culling effective. Merge a building, a
 * grove, a stretch of road — never the whole town.
 *
 * Anything merged here can no longer be animated per-part or picked per-part.
 * That is exactly what "static" means. Interactive things are placeables and go
 * through the build system instead.
 */

import * as THREE from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { group } from '../render/kit'

interface Bucket {
  material: THREE.Material
  castShadow: boolean
  receiveShadow: boolean
  geometries: THREE.BufferGeometry[]
}

export interface MergeStats {
  meshesIn: number
  meshesOut: number
}

const stats: MergeStats = { meshesIn: 0, meshesOut: 0 }

/** Cumulative merge accounting, reported by the harness. */
export function mergeStats(): MergeStats {
  return { ...stats }
}

/**
 * Collapse `src` into as few meshes as possible. `src` is consumed: its meshes
 * are read (world matrices applied) and the returned group replaces it.
 *
 * Materials are SHARED kit materials, so they are never disposed here.
 */
export interface MergeOptions {
  /**
   * Force shadow casting off for the whole cluster. IMPORTANT for perf: three
   * frustum-culls the shadow pass per object, and a merged cluster's bounding
   * sphere is huge, so a merged cluster is almost never culled out of the
   * shadow map. Scenery therefore stops casting; only the buildings do.
   */
  castShadow?: boolean
  receiveShadow?: boolean
}

export function mergeStatic(
  src: THREE.Object3D,
  name: string,
  opts: MergeOptions = {},
): THREE.Group {
  src.updateMatrixWorld(true)
  const buckets = new Map<string, Bucket>()
  let meshesIn = 0

  src.traverse((o) => {
    const mesh = o as THREE.Mesh
    if (mesh.isMesh !== true) return
    const material = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material
    if (material === undefined) return
    const geo = mesh.geometry.clone()
    geo.applyMatrix4(mesh.matrixWorld)
    // Merging needs identical attribute sets; kit geometry only ever carries
    // position/normal/uv, so drop anything else a caller may have added.
    for (const key of Object.keys(geo.attributes)) {
      if (key !== 'position' && key !== 'normal' && key !== 'uv') geo.deleteAttribute(key)
    }
    const cast = opts.castShadow ?? mesh.castShadow
    const receive = opts.receiveShadow ?? mesh.receiveShadow
    const key = `${material.uuid}|${cast ? 1 : 0}|${receive ? 1 : 0}`
    const bucket = buckets.get(key)
    if (bucket === undefined) {
      buckets.set(key, {
        material,
        castShadow: cast,
        receiveShadow: receive,
        geometries: [geo],
      })
    } else {
      bucket.geometries.push(geo)
    }
    meshesIn++
  })

  const out = group(name)
  for (const bucket of buckets.values()) {
    let merged: THREE.BufferGeometry
    if (bucket.geometries.length === 1) {
      const only = bucket.geometries[0]
      if (only === undefined) continue
      merged = only
    } else {
      try {
        merged = mergeGeometries(bucket.geometries, false)
      } catch (err) {
        // A merge failure must never lose the art — fall back to the pieces.
        console.warn(`[world] merge "${name}" fell back to unmerged meshes`, err)
        for (const g of bucket.geometries) {
          const m = new THREE.Mesh(g, bucket.material)
          m.castShadow = bucket.castShadow
          m.receiveShadow = bucket.receiveShadow
          out.add(m)
        }
        continue
      }
      for (const g of bucket.geometries) g.dispose()
    }
    const mesh = new THREE.Mesh(merged, bucket.material)
    mesh.castShadow = bucket.castShadow
    mesh.receiveShadow = bucket.receiveShadow
    mesh.name = `${name}:part${out.children.length}`
    out.add(mesh)
  }

  stats.meshesIn += meshesIn
  stats.meshesOut += out.children.length
  return out
}

/** Deterministic little RNG so the town looks the same every single boot. */
export function seededRandom(seed: number): () => number {
  let s = seed >>> 0
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0
    return s / 0x100000000
  }
}
